import { Tooltip } from 'primereact/tooltip';
import { useState } from 'react';
import type { SidebarOrg } from '../../types';

interface SidebarOrgSwitcherProps {
  org: SidebarOrg;
  isCollapsed: boolean;
  /** Supply to turn this into a real switcher. Needs a tenant API first. */
  onSwitch?: () => void;
}

/**
 * The comp's org row. Static for now: rendering a button that does nothing (or
 * a permanently disabled one) would be a fake affordance, so the chevron is
 * decorative until there is something to switch to.
 */
export function SidebarOrgSwitcher({ org, isCollapsed, onSwitch }: SidebarOrgSwitcherProps) {
  const isPlaceholderName = !org.name || org.name === 'Organization Name';
  const displayName = org.name || 'Organization Name';
  /* The logo that failed to load: the placeholder stands in for it, as for no logo at all. */
  const [failedLogo, setFailedLogo] = useState<string | null>(null);

  const content = (
    <>
      <span className="sidebar__org-left">
        {org.logoUrl && org.logoUrl !== failedLogo ? (
          <img
            className="sidebar__org-logo"
            src={org.logoUrl}
            alt=""
            onError={() => {
              setFailedLogo(org.logoUrl ?? null);
            }}
          />
        ) : (
          <span className="sidebar__org-logo-placeholder">
            <i className="pi pi-building" aria-hidden="true" />
          </span>
        )}
        {!isCollapsed && (
          <span
            className={`sidebar__org-name${isPlaceholderName ? ' sidebar__org-name--placeholder' : ''}`}
            data-pr-tooltip={displayName}
          >
            {displayName}
          </span>
        )}
      </span>
      {!isCollapsed && onSwitch && (
        <i className="pi pi-chevron-down sidebar__org-chevron" aria-hidden="true" />
      )}
    </>
  );

  if (onSwitch) {
    return (
      <>
        <Tooltip target=".sidebar__org[data-pr-tooltip], .sidebar__org [data-pr-tooltip]" position="right" />
        <button
          type="button"
          className="sidebar__org"
          onClick={onSwitch}
          aria-haspopup="listbox"
          data-pr-tooltip={displayName}
        >
          {content}
        </button>
      </>
    );
  }

  return (
    <>
      <Tooltip target=".sidebar__org[data-pr-tooltip], .sidebar__org [data-pr-tooltip]" position="right" />
      <div className="sidebar__org" data-pr-tooltip={displayName}>
        {content}
      </div>
    </>
  );
}

